import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UsageDashboard = () => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    fetchUsage();
    fetchPlans();
  }, []);

  useEffect(() => {
    if (!usage?.cycle?.reset_at) return;
    const resetAt = new Date(usage.cycle.reset_at).getTime();
    const tick = () => setSecondsLeft(Math.max(0, Math.floor((resetAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [usage]);

  const formatCountdown = (total) => {
    const d = Math.floor(total / 86400);
    const h = Math.floor((total % 86400) / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return `${d}d ${pad(h)}h ${pad(m)}m ${pad(s)}s`;
  };

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/usage/summary`);
      setUsage(response.data);
    } catch (error) {
      console.error('Error fetching usage:', error);
      toast.error('Erro ao carregar dados de uso');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!usage) {
    return null;
  }

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900">📊 Seu Uso & Limites</h2>
        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
          usage.plan.id === 'enterprise' ? 'bg-purple-100 text-purple-800' :
          usage.plan.id === 'pro' ? 'bg-blue-100 text-blue-800' :
          usage.plan.id === 'starter' ? 'bg-green-100 text-green-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {usage.plan.name}
        </span>
      </div>

      {/* Reset countdown */}
      {usage.cycle && (
        <div className="mb-6 flex items-center justify-between p-4 rounded-lg bg-slate-900 text-white" data-testid="usage-reset-countdown">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">Seus limites renovam em</p>
            <p className="text-2xl font-bold tabular-nums mt-1">{formatCountdown(secondsLeft)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400">Próxima renovação</p>
            <p className="text-sm font-semibold">{new Date(usage.cycle.reset_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' })}</p>
            <p className="text-xs text-slate-400">00:00 (UTC)</p>
          </div>
        </div>
      )}

      {/* Links Usage */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">🔗 Links de Afiliados</span>
          <span className="text-sm text-gray-600" data-testid="usage-links-count">
            {usage.links.unlimited ? (
              <span className="text-green-600 font-semibold">∞ Ilimitado</span>
            ) : (
              <>{usage.links.used} / {usage.links.limit} <span className="text-green-700 font-semibold">· restam {usage.links.remaining}</span></>
            )}
          </span>
        </div>
        {!usage.links.unlimited && (
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(usage.links.percentage)}`}
              style={{ width: `${Math.min(100, usage.links.percentage)}%` }}
            ></div>
          </div>
        )}
        {!usage.links.unlimited && usage.links.percentage >= 80 && (
          <p className="text-xs text-orange-600 mt-1">
            ⚠️ Você está usando {usage.links.percentage.toFixed(0)}% dos seus links. 
            {usage.links.remaining > 0 ? ` Restam ${usage.links.remaining} links.` : ' Limite atingido!'}
          </p>
        )}
      </div>

      {/* AI Content Usage */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">🤖 Conteúdo IA</span>
          <span className="text-sm text-gray-600" data-testid="usage-ai-count">
            {usage.ai_content.unlimited ? (
              <span className="text-green-600 font-semibold">∞ Ilimitado</span>
            ) : (
              <>{usage.ai_content.used} / {usage.ai_content.limit} <span className="text-green-700 font-semibold">· restam {usage.ai_content.remaining}</span></>
            )}
          </span>
        </div>
        {!usage.ai_content.unlimited && (
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(usage.ai_content.percentage)}`}
              style={{ width: `${Math.min(100, usage.ai_content.percentage)}%` }}
            ></div>
          </div>
        )}
        {!usage.ai_content.unlimited && usage.ai_content.percentage >= 80 && (
          <p className="text-xs text-orange-600 mt-1">
            ⚠️ Você está usando {usage.ai_content.percentage.toFixed(0)}% dos seus conteúdos IA. 
            {usage.ai_content.remaining > 0 ? ` Restam ${usage.ai_content.remaining} conteúdos.` : ' Limite atingido!'}
          </p>
        )}
      </div>

      {/* Plan Features */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">✨ Recursos do seu plano:</h3>
        <ul className="text-sm text-gray-600 space-y-1">
          {usage.plan.features.map((feature, index) => (
            <li key={index} className="flex items-center">
              <span className="text-green-500 mr-2">✓</span>
              {feature}
            </li>
          ))}
        </ul>
      </div>

      {/* Upgrade CTA */}
      {usage.upgrade_available && (
        <div className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
          <h3 className="text-sm font-semibold text-yellow-800 mb-2">🚀 Quer mais recursos?</h3>
          <p className="text-sm text-yellow-700 mb-3">
            Faça upgrade para o plano {usage.next_plan === 'pro' ? 'Pro' : 'Enterprise'} e desbloqueie mais links e conteúdos IA!
          </p>
          <a 
            href="/upgrade"
            className="inline-block bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:from-yellow-600 hover:to-orange-700 transition-all"
          >
            Ver Planos de Upgrade →
          </a>
        </div>
      )}

      {/* All Plans Comparison */}
      {plans.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">📋 Comparação de Planos:</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <div 
                key={plan.id}
                className={`p-4 rounded-lg border-2 ${
                  plan.id === usage.plan.id 
                    ? 'border-blue-500 bg-blue-50' 
                    : plan.popular 
                      ? 'border-yellow-400 bg-yellow-50' 
                      : 'border-gray-200 bg-white'
                }`}
              >
                {plan.popular && (
                  <span className="text-xs bg-yellow-500 text-white px-2 py-1 rounded-full">
                    Mais Popular
                  </span>
                )}
                {plan.id === usage.plan.id && (
                  <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                    Seu Plano
                  </span>
                )}
                <h4 className="font-bold text-gray-900 mt-2">{plan.name}</h4>
                <p className="text-2xl font-bold text-gray-900 my-2">
                  R$ {plan.price}<span className="text-sm font-normal">/mês</span>
                </p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>🔗 {plan.link_limit === -1 ? 'Links ilimitados' : `${plan.link_limit} links`}</li>
                  <li>🤖 {plan.ai_content_limit === -1 ? 'Conteúdo ilimitado' : `${plan.ai_content_limit} conteúdos IA`}</li>
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageDashboard;
